<template>
	<div
		class="relative flex-grow h-full p-4 overflow-hidden bg-mx-gray-100 dark:bg-mx-green-600 rounded-xl shrink-0"
		:style="{ minWidth: `${DEVICE_TOOL_CARD_WIDTH}px` }"
	>
		<!-- Gradient Background Blobs (only show with results, not during loading) -->
		<span
			v-if="showResult && gradientColor"
			aria-hidden="true"
			class="absolute w-40 h-40 transition-colors duration-700 rounded-full pointer-events-none -bottom-16 right-8 blur-3xl"
			:style="{ backgroundColor: `${gradientColor}70` }"
		/>
		<span
			v-if="showResult && gradientColor"
			aria-hidden="true"
			class="absolute left-0 w-48 h-48 transition-colors duration-700 rounded-full pointer-events-none -top-24 blur-3xl"
			:style="{ backgroundColor: `${gradientColor}60` }"
		/>

		<div
			class="flex flex-col items-center justify-start flex-grow mx-auto shrink-0"
			:style="{ width: `${DEVICE_TOOL_CARD_WIDTH}px` }"
		>
			<!-- Title Section -->
			<div class="text-sm font-semibold tracking-wide text-center uppercase place-self-center text-mx-gray-500 dark:text-mx-gray-300">
				<slot name="title" />
			</div>

			<!-- Hint Slot (for interactive hints when idle) -->
			<slot name="hint">
			<div
				v-if="i18nPrefix"
				class="flex items-center justify-center w-full h-10"
			>
				<!-- Cooldown Timer -->
				<span
					v-if="cooldownSeconds > 0 && !isLoading"
					class="text-xs font-medium text-center text-mx-gray-400"
				>
					{{ t(`${i18nPrefix}.cooldown`, { seconds: cooldownSeconds }) }}
				</span>
				<!-- Hint Text -->
				<span
					v-else-if="!isLoading && (!requiresSerialNumber || hasSerialNumber) && isDeviceSupported"
					class="text-xs font-medium text-center hint-text text-mx-gray-500 dark:text-mx-gray-400"
				>
					<slot name="hint-text">{{ t(`${i18nPrefix}.hint`) }}</slot>
				</span>
			</div>
		</slot>

			<!-- Button Section -->
			<div class="relative flex items-center justify-center w-full py-2">
				<template v-if="buttonConfig">
				<!-- Default button implementation -->
				<NeumorphicButton
					v-if="!buttonConfig.hideWhenUnsupported || isDeviceSupported"
					:icon="buttonConfig.icon"
					:is-loading="isLoading"
					:disabled="isButtonDisabled"
					:style="buttonIconStyle"
					:result-color="buttonResultColor"
					:label="t(buttonConfig.labelKey)"
					:class="{ 'cursor-not-allowed': isInCooldown }"
					size="md"
					@click="handleButtonClick"
				/>

				<!-- Unsupported state message -->
				<div
					v-else-if="showUnsupportedMessage && unsupportedMessageKey"
					class="flex flex-col items-center justify-center gap-2 py-4 text-center"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						stroke-width="1.5"
						stroke="currentColor"
						class="w-12 h-12 text-[#F53A3A]"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"
						/>
					</svg>
					<span class="text-sm font-medium text-mx-gray-500 dark:text-mx-gray-400">
						{{ t(unsupportedMessageKey) }}
					</span>
				</div>
			</template>

			<!-- Allow parent to override with custom slot -->
			<slot v-else name="button" />
			</div>

			<!-- Progress Bar OR Status Display -->
			<div class="flex items-center justify-center w-full h-10">
				<div
					v-if="props.progressBar && showProgress"
					class="w-full"
				>
					<EquipmentSharedProgressBar :progress="progress" />
				</div>
				<slot
					v-else
					name="status"
				>
					<div
						v-if="i18nPrefix"
						class="flex items-center justify-center gap-2 -ml-3"
					>
						<!-- Loading State (operation in progress) -->
						<template v-if="isLoading">
							<ClockIcon
								class="w-5 h-5"
								:style="{ color: SEVERITY_COLORS.default }"
							/>
							<span
								class="text-lg font-bold capitalize"
								:style="{ color: SEVERITY_COLORS.default }"
							>
								{{ statusLabel }}
							</span>
						</template>

						<!-- Waiting State (no result yet, only for supported devices) -->
						<template v-else-if="!showResult && isDeviceSupported">
							<ClockIcon
								class="w-5 h-5"
								:style="{ color: SEVERITY_COLORS.default }"
							/>
							<span
								class="text-lg font-bold capitalize"
								:style="{ color: SEVERITY_COLORS.default }"
							>
								{{ statusLabel || t(`${i18nPrefix}.labels.waiting`) }}
							</span>
						</template>

						<!-- Result State (after completion) -->
						<template v-else-if="showResult">
							<component
								:is="iconMap[severity] || ClockIcon"
								class="w-5 h-5"
								:style="{ color: severityColor }"
							/>
							<span
								class="text-lg font-bold capitalize"
								:style="{ color: severityColor }"
							>
								{{ statusLabel }}
							</span>
						</template>
					</div>
				</slot>
			</div>

			<!-- Result Content (3 fixed-height lines) -->
			<div class="flex flex-col self-center w-full">
				<!-- Missing Serial Number Warning (only for supported devices that require it) -->
				<div
					v-if="isDeviceSupported && requiresSerialNumber && !hasSerialNumber && progress === 0"
					class="flex items-center justify-center min-h-6 text-[#CCAC3B] text-xs text-center"
				>
					{{ t(errorKeys.missingSerial) }}
				</div>


				<!-- Result Content Slot (handles all states including errors, only for supported devices) -->
				<slot
					v-else-if="isDeviceSupported"
					name="result"
				>
					<!-- Default 3-line result structure with sub-slots -->
					<template v-if="i18nPrefix">
						<!-- Line 1: Description -->
						<div class="flex items-center justify-center text-xs text-center min-h-6 text-mx-gray-500 dark:text-mx-gray-400">
							<span v-if="isLoading">
								<slot name="result-line1-loading">{{ t(`${i18nPrefix}.in-progress.reaching`) }}</slot>
							</span>
							<span v-else>
								<slot name="result-line1">{{ t(`${i18nPrefix}.descriptions.waiting`) }}</slot>
							</span>
						</div>

						<!-- Line 2: Message -->
						<div class="flex items-center justify-center text-xs font-medium text-center min-h-6 text-mx-gray-500 dark:text-mx-gray-300">
							<span v-if="isLoading">
								<slot name="result-line2-loading">{{ t(`${i18nPrefix}.in-progress.waiting`) }}</slot>
							</span>
							<span v-else>
								<slot name="result-line2">{{ t(`${i18nPrefix}.messages.ready-status`) }}</slot>
							</span>
						</div>

						<!-- Line 3: Device info / Action prompt -->
						<div class="flex items-center justify-center gap-1 text-xs min-h-6 text-mx-gray-500 dark:text-mx-gray-400">
							<!-- Loading: skeleton animation -->
							<template v-if="isLoading">
								<slot name="result-line3-loading">
									<span class="w-16 h-4 rounded bg-mx-gray-200 dark:bg-mx-green-700 animate-pulse" />
									<span>•</span>
									<span class="w-16 h-4 rounded bg-mx-gray-200 dark:bg-mx-green-700 animate-pulse" />
								</slot>
							</template>
							<!-- After: device info -->
							<template v-else-if="deviceInfo && showResult">
								<slot name="result-line3-info" :device-info="deviceInfo">
									<span class="font-medium">{{ deviceInfo.type }}</span>
									<span>•</span>
									<span>{{ deviceInfo.brand }}</span>
								</slot>
							</template>
							<!-- Idle: action prompt -->
							<span v-else>
								<slot name="result-line3-action">{{ t(`${i18nPrefix}.ready-action`) }}</slot>
							</span>
						</div>
					</template>
				</slot>
			</div>
		</div>
		<!-- Additional Details Slot (optional) -->
		<slot name="additional-details" />
	</div>
</template>

<script setup lang="ts">
import NeumorphicButton from '@/components/NeumorphicButton.vue'
import { getSeverityColor, getSeverityStateVariant, SEVERITY_COLORS, STATE_VARIANTS } from '@/utils/equipment/severityColors'
import { CheckCircleIcon, ClockIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/vue/20/solid'
import type { Component } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Icon mapping based on severity
const iconMap: Record<string, typeof ClockIcon> = {
	default: ClockIcon,
	initial: ClockIcon,
	info: CheckCircleIcon,
	ok: CheckCircleIcon,
	healthy: CheckCircleIcon,
	minor: ExclamationTriangleIcon,
	warning: ExclamationTriangleIcon,
	major: XCircleIcon,
	error: XCircleIcon,
	critical: XCircleIcon,
	disconnected: XCircleIcon
}

const DEVICE_TOOL_CARD_WIDTH = 216 // pixels

interface Props {
	/** Current progress (0-100) */
	progress: number
	/** Whether to show the progress bar display */
	progressBar?: boolean
	/** Whether the operation is currently in progress */
	isLoading?: boolean
	/** Whether the device is supported for this operation */
	isDeviceSupported: boolean
	/** Whether the device has a serial number */
	hasSerialNumber: boolean
	/** Whether the device requires a serial number (UISP doesn't, Calix does) */
	requiresSerialNumber?: boolean
	/** Status text to display during operation */
	statusText: string
	/** Whether to show the result */
	showResult: boolean
	/** i18n keys for error messages */
	errorKeys: {
		notSupported: string
		missingSerial: string
		failed: string
	}
	/** Base i18n prefix (e.g., 'device-tools.status') */
	i18nPrefix?: string
	/** Cooldown seconds for hint slot */
	cooldownSeconds?: number
	/** Custom status label */
	statusLabel?: string
	/** Device info for result line 3 */
	deviceInfo?: {
		type?: string
		brand?: string
	} | null
	/** Severity for color styling */
	severity?: string
	/** Button configuration for default button implementation */
	buttonConfig?: {
		labelKey: string
		icon: Component
		hideWhenUnsupported?: boolean
	} | null
	/** Whether to show unsupported state message in button slot */
	showUnsupportedMessage?: boolean
	/** i18n key for unsupported message */
	unsupportedMessageKey?: string
}

const props = withDefaults(defineProps<Props>(), {
	isLoading: false,
	progressBar: false,
	requiresSerialNumber: true,
	i18nPrefix: '',
	cooldownSeconds: 0,
	statusLabel: '',
	deviceInfo: null,
	severity: 'default',
	buttonConfig: null,
	showUnsupportedMessage: false,
	unsupportedMessageKey: ''
})

// Compute colors internally from severity
const severityColor = computed(() => getSeverityColor(props.severity || 'default'))
const gradientColor = computed(() => {
	const variantKey = getSeverityStateVariant(props.severity || 'default')
	return STATE_VARIANTS[variantKey].gradientColor
})

// Button state and styling
const isButtonDisabled = computed(() =>
	props.isLoading ||
	(props.requiresSerialNumber && !props.hasSerialNumber) ||
	!props.isDeviceSupported ||
	props.cooldownSeconds > 0
)

const isInCooldown = computed(() => props.cooldownSeconds > 0)

const buttonIconColor = computed(() => {
	const sev = props.severity
	if (sev === 'ok' || sev === 'info') return '#21c65f'
	if (sev === 'error' || sev === 'critical') return '#ef4444'
	return severityColor.value || ''
})

const buttonIconStyle = computed(() => ({
	'--icon-color': buttonIconColor.value
}))

const buttonResultColor = computed(() => {
	if (!isInCooldown.value) return null
	return buttonIconColor.value || null
})

const emit = defineEmits<{
	'button-click': []
}>()

const handleButtonClick = () => {
	emit('button-click')
}

const showProgress = computed(() => props.isLoading)
</script>

<!-- Global keyframe animations for hint text (shared across device tool components) -->
<style>
@keyframes hintShimmer {
	0%, 100% {
		background-position: -200% center;
	}
	50% {
		background-position: 200% center;
	}
}

@keyframes hintGlow {
	0%, 100% {
		opacity: 0.7;
	}
	50% {
		opacity: 1;
	}
}
</style>

<style scoped>
.hint-text {
	background: linear-gradient(
		90deg,
		currentColor 0%,
		currentColor 40%,
		rgba(195, 195, 195, 0.9) 50%,
		currentColor 60%,
		currentColor 100%
	);
	background-size: 200% auto;
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	animation: hintShimmer 3s ease-in-out infinite;
}

/* Fallback for browsers that don't support background-clip: text */
@supports not (background-clip: text) {
	.hint-text {
		background: none;
		-webkit-text-fill-color: currentColor;
		animation: hintGlow 2.5s ease-in-out infinite;
		color: #c3c3c3;
	}
}

.dark .hint-text {
	background: linear-gradient(
		90deg,
		currentColor 0%,
		currentColor 40%,
		rgba(255, 255, 255, 0.9) 50%,
		currentColor 60%,
		currentColor 100%
	);
	background-size: 200% auto;
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	animation: hintShimmer 3s ease-in-out infinite;
}

/* Fallback for browsers that don't support background-clip: text */
@supports not (background-clip: text) {
	.dark .hint-text {
		background: none;
		-webkit-text-fill-color: currentColor;
		animation: hintGlow 2.5s ease-in-out infinite;
		color: #ffffff;
	}
}
</style>
